import type { User, UserRole } from '@owl/shared';
import { USER_ROLES } from '@owl/shared';
import { useCallback, useState } from 'react';
import { useConfirm } from '../components/Confirm';
import type { ShellCtx } from '../components/Shell';
import { useToast } from '../components/Toast';
import { Icon } from '../components/icons';
import { ApiError, api } from '../lib/api';
import { useAutoFocus } from '../lib/useAutoFocus';
import { useEscapeKey } from '../lib/useEscapeKey';
import { useLoader } from '../lib/useLoader';

function roleLabel(role: UserRole): string {
  switch (role) {
    case 'superadmin':
      return 'Super admin';
    case 'admin':
      return 'Admin';
    case 'viewer':
      return 'Viewer';
  }
}

export function Users({ ctx, currentUser }: { ctx: ShellCtx; currentUser: User }) {
  const refreshTick = ctx.refreshTick;
  const [items, setItems] = useState<User[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [resetting, setResetting] = useState<User | null>(null);
  const { push } = useToast();
  const confirm = useConfirm();

  const load = useCallback(async () => {
    try {
      const { items } = await api.listUsers();
      setItems(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  }, []);

  useLoader(load, refreshTick);

  async function handleDelete(user: User) {
    const ok = await confirm({
      title: 'Delete user',
      sub: user.email,
      body: 'This user loses access immediately. The account cannot be restored.',
      confirmLabel: 'Delete user',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.deleteUser(user.id);
      push(`${user.email} deleted`);
      await load();
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Delete failed');
    }
  }

  return (
    <div className="view-pad">
      <div className="view-head">
        <div>
          <div className="view-title">Users</div>
          <div className="view-sub">
            {items?.length ?? 0} user{(items?.length ?? 0) === 1 ? '' : 's'} · roles drive what each
            account can see and modify
          </div>
        </div>
        <button type="button" className="cta" onClick={() => setCreating(true)}>
          <Icon.Plus size={13} /> New user
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {items === null ? (
        <div className="empty-state">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty-state">No users yet.</div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((u) => {
                const isSelf = u.id === currentUser.id;
                return (
                  <tr key={u.id} className="data-row">
                    <td>
                      <strong>{u.email}</strong>
                      {isSelf && <span className="dim"> · you</span>}
                    </td>
                    <td>
                      <span className="dim">{roleLabel(u.role)}</span>
                    </td>
                    <td className="dim">{new Date(u.createdAt).toLocaleDateString('es-ES')}</td>
                    <td>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="icon-btn"
                          title="Edit role / email"
                          onClick={() => setEditing(u)}
                        >
                          <Icon.Edit size={12} />
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          title="Reset password"
                          onClick={() => setResetting(u)}
                        >
                          <Icon.Refresh size={12} />
                        </button>
                        {!isSelf && (
                          <button
                            type="button"
                            className="icon-btn"
                            title="Delete"
                            onClick={() => handleDelete(u)}
                          >
                            <Icon.Trash size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load();
          }}
        />
      )}

      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {resetting && (
        <ResetPasswordModal
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={() => setResetting(null)}
        />
      )}
    </div>
  );
}

function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  useEscapeKey(onClose);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('viewer');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoFocusRef = useAutoFocus<HTMLInputElement>();
  const { push } = useToast();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api.createUser({ email: email.trim(), password, role });
      push(`${email} created`);
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance. The dialog is dismissable from the keyboard via useEscapeKey and the header close button, so the backdrop needs no key handler of its own.
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-card">
        <div className="modal-head">
          <div className="modal-title">New user</div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <Icon.X size={14} />
          </button>
        </div>
        <form onSubmit={submit} className="modal-body">
          <label className="form-row">
            <span className="form-label">Email</span>
            <input
              className="form-input"
              type="email"
              ref={autoFocusRef}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label className="form-row">
            <span className="form-label">Password</span>
            <input
              className="form-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
            />
            <div className="hint">Minimum 8 characters. The user can change it later.</div>
          </label>
          <fieldset className="form-row">
            <legend className="form-label">Role</legend>
            <div className="range-picker" style={{ width: 'fit-content' }}>
              {USER_ROLES.map((r) => (
                <button
                  type="button"
                  key={r}
                  className={`range-opt ${role === r ? 'active' : ''}`}
                  onClick={() => setRole(r)}
                >
                  {roleLabel(r)}
                </button>
              ))}
            </div>
          </fieldset>
          {error && (
            <div className="error-banner" style={{ marginBottom: 0 }}>
              {error}
            </div>
          )}
          <div className="modal-foot" style={{ padding: 0, border: 0 }}>
            <button type="button" className="cta-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="cta" disabled={busy}>
              {busy ? 'Creating…' : 'Create user'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: User;
  onClose: () => void;
  onSaved: () => void;
}) {
  useEscapeKey(onClose);
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState<UserRole>(user.role);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoFocusRef = useAutoFocus<HTMLInputElement>();
  const { push } = useToast();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const patch: { email?: string; role?: UserRole } = {};
    if (email.trim() !== user.email) patch.email = email.trim();
    if (role !== user.role) patch.role = role;
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api.updateUser(user.id, patch);
      push(`${user.email} updated`);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance. The dialog is dismissable from the keyboard via useEscapeKey and the header close button, so the backdrop needs no key handler of its own.
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-card">
        <div className="modal-head">
          <div className="modal-title">Edit user</div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <Icon.X size={14} />
          </button>
        </div>
        <form onSubmit={submit} className="modal-body">
          <label className="form-row">
            <span className="form-label">Email</span>
            <input
              className="form-input"
              type="email"
              ref={autoFocusRef}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <fieldset className="form-row">
            <legend className="form-label">Role</legend>
            <div className="range-picker" style={{ width: 'fit-content' }}>
              {USER_ROLES.map((r) => (
                <button
                  type="button"
                  key={r}
                  className={`range-opt ${role === r ? 'active' : ''}`}
                  onClick={() => setRole(r)}
                >
                  {roleLabel(r)}
                </button>
              ))}
            </div>
            <div className="hint">Demoting the last superadmin is blocked by the server.</div>
          </fieldset>
          {error && (
            <div className="error-banner" style={{ marginBottom: 0 }}>
              {error}
            </div>
          )}
          <div className="modal-foot" style={{ padding: 0, border: 0 }}>
            <button type="button" className="cta-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="cta" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordModal({
  user,
  onClose,
  onDone,
}: {
  user: User;
  onClose: () => void;
  onDone: () => void;
}) {
  useEscapeKey(onClose);
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoFocusRef = useAutoFocus<HTMLInputElement>();
  const { push } = useToast();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api.resetUserPassword(user.id, newPassword);
      push(`${user.email} password reset`);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Reset failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only affordance. The dialog is dismissable from the keyboard via useEscapeKey and the header close button, so the backdrop needs no key handler of its own.
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-card">
        <div className="modal-head">
          <div className="modal-title">Reset password</div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <Icon.X size={14} />
          </button>
        </div>
        <form onSubmit={submit} className="modal-body">
          <label className="form-row">
            <span className="form-label">New password for {user.email}</span>
            <input
              className="form-input"
              type="password"
              ref={autoFocusRef}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={8}
              required
            />
            <div className="hint">
              Minimum 8 characters. The user will need to use it next login.
            </div>
          </label>
          {error && (
            <div className="error-banner" style={{ marginBottom: 0 }}>
              {error}
            </div>
          )}
          <div className="modal-foot" style={{ padding: 0, border: 0 }}>
            <button type="button" className="cta-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="cta" disabled={busy}>
              {busy ? 'Saving…' : 'Reset password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
